import React, { useState, useEffect, useCallback } from "react";
import FilterDropdowns from "../../components/FilterDropdowns/FilterDropdowns";
import FindGraduateSchoolsTiles from "./FindGraduateSchoolsTiles";
import { filterGraduateSchoolsEventBuilder, sendEvent } from "../../utils/event-helpers";
import { fetchResourceTilesByTagFilters, TaggedResourceTiles } from "../../utils/contentful-helpers";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { markRecommendationFiltersAdjusted } from "../../stores/recommendations/mentorRecommendationsSlice";
import { useOutletContext } from "react-router-dom";
import { RecommenderOutletContext } from "../RecommenderPage/RecommenderPage";


const FindGraduateSchools = () => {
  const [taggedResourceTiles, setTaggedResourceTiles] = useState<TaggedResourceTiles[]>([]);
  const { pageFilters, setPageFilters } = useOutletContext<RecommenderOutletContext>();
  
  const selectedSubfields = pageFilters.findGraduateSchools.subfields;
  const selectedDegrees = pageFilters.findGraduateSchools.degrees;

  const { userId, sessionId } = useAppSelector((state) => state.session);
  const recommendations = useAppSelector((state) => state.mentorRecommendations.data);
  const dispatch = useAppDispatch();

  const updateGraduateSchoolFilters = useCallback(
    (updates: { subfields?: string[], degrees?: string[] }) => {
      setPageFilters((prev) => ({
        ...prev,
        findGraduateSchools: {
          ...prev.findGraduateSchools,
          ...updates,
        },
      }));
    },
    [setPageFilters]
  );

  const filteredResourceTiles = taggedResourceTiles.map((group) => {
    if (selectedSubfields.length === 0 && selectedDegrees.length === 0) {
      return group;
    }

    return {
      ...group,
      data: group.data.filter((tile) => {
        const keywords = tile.data.keyword || [];

        const matchesSubfield =
          selectedSubfields.length > 0
            ? selectedSubfields.some((sub) => keywords.includes(sub))
            : true;

        const matchesDegree =
          selectedDegrees.length > 0
            ? selectedDegrees.some((deg) => keywords.includes(deg))
            : true;

        return matchesSubfield && matchesDegree;
      }),
    };
  });

  useEffect(() => {
    if (!recommendations) return;
    if (recommendations.recommendedFilters.adjusted) return;

    updateGraduateSchoolFilters({
      subfields: recommendations.recommendedFilters.subFields,
      degrees: recommendations.recommendedFilters.degrees,
    });
  }, [recommendations, updateGraduateSchoolFilters])
  
  useEffect(() => {
    const tagFilters = ["Degree General Resources", "Degree Additional Resources"];
    fetchResourceTilesByTagFilters(tagFilters).then((results) => {
      setTaggedResourceTiles(results);
    });
  }, []);

  useEffect(() => {
    const allFilteredTiles = filteredResourceTiles.map(tile => tile.data).flat();
    const allTileTitles = allFilteredTiles.map(tile => tile.title);

    const eventData = filterGraduateSchoolsEventBuilder(userId, sessionId, allTileTitles, {
      degreesFilter: selectedDegrees,
      subfieldsFilter: selectedSubfields,
    });

    sendEvent(eventData);
  }, [userId, sessionId, selectedDegrees, selectedSubfields, filteredResourceTiles])

  const handleSubfieldsChange = (subfields: string[]) => {
    dispatch(markRecommendationFiltersAdjusted());
    updateGraduateSchoolFilters({ subfields })
  };

  const handleDegreesChange = (degrees: string[]) => {
    dispatch(markRecommendationFiltersAdjusted());
    updateGraduateSchoolFilters({ degrees })
  };

  const handleClearFilters = () => {
    dispatch(markRecommendationFiltersAdjusted());
    updateGraduateSchoolFilters({ subfields: [], degrees: [] })
  };

  return (
    <>
      <div className="explore-careers">
        <div className="resources-container">
          <div className="align-headers">
            <div>
              <p
                className="career-highlighted-paragraph"
                style={{ paddingBottom: "14px", marginTop: "17px" }}
              >
                Graduate School Resources
                <span style={{ marginRight: "15.5rem" }}></span>
              </p>
            </div>

            <div className="dropdown-list">
              <div className="align-input">
                <FilterDropdowns
                  showDegree={true}
                  showSubfield={true}
                  selectedDegrees={selectedDegrees}
                  selectedSubfields={selectedSubfields}
                  onDegreesChange={handleDegreesChange}
                  onSubfieldsChange={handleSubfieldsChange}
                  onClear={handleClearFilters}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="gap"></div>
        {/* Toggle Section Links for Mobile */}
        <div className="section-links">
          <a href="#general-resources" className="link-item">
            General Resources
          </a>
          <a href="#additional-resources" className="link-item">
            Subfield-Based Resources
          </a>
        </div>
      </div>

      <div>
        {filteredResourceTiles.map(tile => (
          <FindGraduateSchoolsTiles
            key={tile.tagFilter}
            resourceTiles={tile.data}
          />
        ))}
      </div>
    </>
  );
};

export default FindGraduateSchools;
